import { useRef, useState } from 'react'
import { Box, Flex, Image, Text, VStack } from '@chakra-ui/react'

import { useCreateCollection, useUpdateCollection } from '@/api/collections'
import { useUploadFile } from '@/api/storage'
import IconUpload from '@/components/icons/IconUpload'
import Button from '@/components/ui/Button'
import FormInput from '@/components/ui/form/FormInput'
import Modal from '@/components/ui/Modal'

import type { Collection } from '@/api/collections'

interface IPGroupModalProps {
  isOpen: boolean
  onClose: () => void
  collection?: Collection | null
}

export default function IPGroupModal({
  isOpen,
  onClose,
  collection
}: IPGroupModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={collection ? 'Edit IP Group' : 'Create IP Group'}
    >
      {isOpen && (
        <IPGroupForm
          collection={collection}
          onClose={onClose}
        />
      )}
    </Modal>
  )
}

interface IPGroupFormProps {
  collection?: Collection | null
  onClose: () => void
}

const ACCEPTED_TYPES = '.png,.jpeg,.jpg'

function IPGroupForm({ collection, onClose }: IPGroupFormProps) {
  const isEditMode = !!collection

  const [name, setName] = useState(collection?.name ?? '')
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(
    collection?.cardImageUrl ?? null
  )
  const fileInputRef = useRef<HTMLInputElement>(null)

  const uploadFile = useUploadFile()
  const createCollection = useCreateCollection()
  const updateCollection = useUpdateCollection()

  const isSubmitting =
    uploadFile.isPending ||
    createCollection.isPending ||
    updateCollection.isPending

  const isFormValid = name.trim().length > 0

  const hasChanges = isEditMode
    ? name.trim() !== collection.name || !!imageFile
    : true

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setImageFile(file)

    const reader = new FileReader()
    reader.onload = (ev) => {
      setImagePreview(ev.target?.result as string)
    }
    reader.readAsDataURL(file)

    e.target.value = ''
  }

  const handleSubmit = async () => {
    if (!isFormValid || isSubmitting) return

    let imageUrl: string | undefined

    if (imageFile) {
      imageUrl = await uploadFile.mutateAsync(imageFile)
    }

    if (isEditMode) {
      await updateCollection.mutateAsync({
        id: collection.id,
        name: name.trim(),
        cardImageUrl: imageUrl
      })
    } else {
      await createCollection.mutateAsync({ name: name.trim(), imageUrl })
    }

    onClose()
  }

  const handleClose = () => {
    if (isSubmitting) return
    onClose()
  }

  const imageFileName = imageFile
    ? imageFile.name
    : collection?.cardImageUrl
      ? (collection.cardImageUrl.split('/').pop() ?? 'Image')
      : 'Not Uploaded'

  return (
    <>
      <Flex direction="column" gap={5}>
        <Flex direction="column" gap={2}>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <Flex
            align="center"
            justify="center"
            h="214px"
            w="full"
            bg={isEditMode && imagePreview ? 'black' : 'lightGray.400'}
            borderWidth={isEditMode && imagePreview ? '0' : '1px'}
            borderStyle="dashed"
            borderColor="brand.600"
            borderRadius="8px"
            cursor="pointer"
            overflow="hidden"
            onClick={() => fileInputRef.current?.click()}
          >
            {imagePreview ? (
              <Image
                src={imagePreview}
                alt="Cover preview"
                objectFit="cover"
                w="full"
                h="full"
              />
            ) : (
              <VStack gap={2}>
                <IconUpload w="40px" h="40px" />
                <Text
                  textStyle="bodySuperSmall"
                  fontWeight={500}
                  color="brand.600"
                  textAlign="center"
                >
                  Drag & drop cover image or click to upload
                </Text>
              </VStack>
            )}
          </Flex>
          {isEditMode && imagePreview ? (
            <Flex align="center" gap={1}>
              <Text textStyle="bodySuperSmall" fontWeight={600}>
                {imageFileName}
              </Text>
              <Text
                textStyle="bodySuperSmall"
                fontWeight={600}
                color="brand.600"
                cursor="pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                Change
              </Text>
            </Flex>
          ) : (
            <Text textStyle="bodySuperSmall" color="secondaryDarkGray.600">
              {imageFile ? imageFile.name : 'Not Uploaded'}
            </Text>
          )}
        </Flex>

        <FormInput
          value={name}
          onChange={(e) => setName(e.target.value)}
          w="full"
          placeholder="IP Group Name"
          aria-labelledby="ip-group-name"
        />
      </Flex>

      {isEditMode ? (
        <Flex mt={5} gap={3}>
          <Button
            flex="1"
            h="46px"
            disabled={!isFormValid || !hasChanges || isSubmitting}
            loading={isSubmitting}
            onClick={handleSubmit}
            variant="primary"
            fontWeight={600}
          >
            Save Changes
          </Button>
          <Button
            variant="secondary"
            flex="1"
            fontWeight={600}
            onClick={handleClose}
          >
            Discard Changes
          </Button>
        </Flex>
      ) : (
        <Box mt={5}>
          <Button
            w="full"
            h="46px"
            disabled={!isFormValid || isSubmitting}
            loading={isSubmitting}
            onClick={handleSubmit}
            variant="primary"
            fontWeight={600}
          >
            Create IP Group
          </Button>
        </Box>
      )}
    </>
  )
}
